import React, { useState } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Plus, Edit, Trash2, Building2, Globe, RefreshCw, Eye, EyeOff, Users, UsersRound, Layout, Bot, CheckCircle2, Loader2, Shield, Key, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import organizationsService from "../../services/organizationsService";
import workvivoService from "../../services/workvivoService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";

const OrganizationsSection = () => {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const [showModal, setShowModal] = useState(false);
  const [showWorkvivoSetup, setShowWorkvivoSetup] = useState(false);
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [resyncOrg, setResyncOrg] = useState(null);
  const [resyncResult, setResyncResult] = useState(null);
  const [showConnectWorkvivo, setShowConnectWorkvivo] = useState(false);
  const [connectOrg, setConnectOrg] = useState(null);
  const [connectResult, setConnectResult] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [setupResult, setSetupResult] = useState(null);
  const [expandedOrgId, setExpandedOrgId] = useState(null);

  // Fetch organizations
  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => organizationsService.list(),
  });

  // Fetch integrations for all organizations
  const integrationQueries = useQueries({
    queries: organizations.map((org) => ({
      queryKey: ["org-integrations", org.id],
      queryFn: () => organizationsService.getIntegrations(org.id),
      enabled: organizations.length > 0,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  // Build a map of org_id -> integration data
  const integrationsMap = {};
  organizations.forEach((org, idx) => {
    const q = integrationQueries[idx];
    if (q?.data) integrationsMap[org.id] = q.data;
  });

  // Create organization mutation
  const createMutation = useMutation({
    mutationFn: (data) => organizationsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Organization created successfully!");
      closeModal();
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Update organization mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => organizationsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Organization updated successfully!");
      closeModal();
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Delete organization mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => organizationsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success("Organization deleted successfully!");
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Workvivo setup mutation
  const workvivoSetupMutation = useMutation({
    mutationFn: (data) => workvivoService.completeSetup(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setSetupResult(response.data);
      toast.success("Workvivo setup completed successfully!");
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Resync Workvivo mutation
  const resyncMutation = useMutation({
    mutationFn: ({ id, credentials }) => organizationsService.resyncWorkvivo(id, credentials),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      const data = response.data || response;
      setResyncResult(data);
      toast.success(`Sync complete! Users: ${data.users_imported || 0}, Teams: ${data.teams_imported || 0}, Spaces: ${data.spaces_imported || 0}`);
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Connect existing org to Workvivo mutation
  const connectWorkvivoMutation = useMutation({
    mutationFn: (data) => workvivoService.completeSetup(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setConnectResult(response.data);
      toast.success("Organization connected to Workvivo successfully!");
    },
    onError: (error) => {
      toast.error(formatError(error));
    },
  });

  // Form handling for connect to Workvivo
  const {
    register: registerConnect,
    handleSubmit: handleConnectSubmit,
    reset: resetConnect,
    formState: { errors: connectErrors }
  } = useForm({
    defaultValues: {
      workvivo_base_url: "",
      workvivo_id: "",
      access_token: "",
    },
  });

  // Form handling for resync
  const {
    register: registerResync,
    handleSubmit: handleResyncSubmit,
    reset: resetResync,
    formState: { errors: resyncErrors }
  } = useForm({
    defaultValues: {
      workvivo_base_url: "",
      workvivo_id: "",
      access_token: "",
    },
  });

  const openResyncModal = (org) => {
    setResyncOrg(org);
    setResyncResult(null);
    resetResync({
      workvivo_base_url: "",
      workvivo_id: "",
      access_token: "",
    });
    setShowResyncModal(true);
    setShowToken(false);
  };

  const closeResyncModal = () => {
    setShowResyncModal(false);
    setResyncOrg(null);
    setResyncResult(null);
    setShowToken(false);
    resetResync();
  };

  const onResyncSubmit = (data) => {
    resyncMutation.mutate({
      id: resyncOrg.id,
      credentials: {
        workvivo_base_url: data.workvivo_base_url,
        workvivo_id: data.workvivo_id,
        access_token: data.access_token,
      }
    });
  };

  const openConnectWorkvivo = (org) => {
    setConnectOrg(org);
    setConnectResult(null);
    resetConnect({
      workvivo_base_url: "",
      workvivo_id: "",
      access_token: "",
    });
    setShowConnectWorkvivo(true);
    setShowToken(false);
  };

  const closeConnectWorkvivo = () => {
    setShowConnectWorkvivo(false);
    setConnectOrg(null);
    setConnectResult(null);
    setShowToken(false);
    resetConnect();
  };

  const onConnectSubmit = (data) => {
    connectWorkvivoMutation.mutate({
      organization_id: connectOrg.id,
      workvivo_base_url: data.workvivo_base_url,
      workvivo_id: data.workvivo_id,
      access_token: data.access_token,
      import_users: true,
      import_teams: true,
      import_spaces: true,
      setup_bot: false,
    });
  };

  // Form handling for basic org
  const { register, handleSubmit: handleFormSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      description: "",
      is_active: true,
      default_nomination_recipient_points: 0,
      default_nomination_sender_points: 0,
    },
  });

  // Form handling for Workvivo setup
  const {
    register: registerWorkvivo,
    handleSubmit: handleWorkvivoSubmit,
    reset: resetWorkvivo,
    formState: { errors: workvivoErrors }
  } = useForm({
    defaultValues: {
      organization_name: "",
      organization_description: "",
      access_token: "",
      workvivo_base_url: "",
      workvivo_id: "",
      default_nomination_recipient_points: 0,
      default_nomination_sender_points: 0,
      import_users: true,
      import_teams: true,
      import_spaces: true,
      setup_bot: true,
      default_space_id: "",
    },
  });

  const onSubmit = (data) => {
    const payload = {
      name: data.name,
      description: data.description || undefined,
      is_active: data.is_active,
      default_nomination_recipient_points: parseInt(data.default_nomination_recipient_points) || 0,
      default_nomination_sender_points: parseInt(data.default_nomination_sender_points) || 0,
    };

    if (editingOrg) {
      updateMutation.mutate({ id: editingOrg.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const onWorkvivoSubmit = (data) => {
    const payload = {
      organization_name: data.organization_name,
      organization_description: data.organization_description || undefined,
      access_token: data.access_token,
      workvivo_base_url: data.workvivo_base_url,
      workvivo_id: data.workvivo_id,
      default_nomination_recipient_points: parseInt(data.default_nomination_recipient_points) || 0,
      default_nomination_sender_points: parseInt(data.default_nomination_sender_points) || 0,
      import_users: data.import_users,
      import_teams: data.import_teams,
      import_spaces: data.import_spaces,
      setup_bot: data.setup_bot,
      default_space_id: data.default_space_id || undefined,
    };

    workvivoSetupMutation.mutate(payload);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this organization? This action cannot be undone.")) {
      deleteMutation.mutate(id);
    }
  };

  const openForCreate = () => {
    setEditingOrg(null);
    reset({
      name: "",
      description: "",
      is_active: true,
      default_nomination_recipient_points: 0,
      default_nomination_sender_points: 0,
    });
    setShowModal(true);
  };

  const openForEdit = (org) => {
    setEditingOrg(org);
    reset({
      name: org.name,
      description: org.description || "",
      is_active: org.is_active,
      default_nomination_recipient_points: org.default_nomination_recipient_points || 0,
      default_nomination_sender_points: org.default_nomination_sender_points || 0,
    });
    setShowModal(true);
  };

  const openWorkvivoSetup = () => {
    resetWorkvivo({
      organization_name: "",
      organization_description: "",
      access_token: "",
      workvivo_base_url: "",
      workvivo_id: "",
      default_nomination_recipient_points: 0,
      default_nomination_sender_points: 0,
      import_users: true,
      import_teams: true,
      import_spaces: true,
      setup_bot: true,
      default_space_id: "",
    });
    setSetupResult(null);
    setShowWorkvivoSetup(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    setShowToken(false);
    reset();
  };

  const closeWorkvivoSetup = () => {
    setShowWorkvivoSetup(false);
    setSetupResult(null);
    setShowToken(false);
    resetWorkvivo();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Organizations</h1>
          <p className="text-sm text-slate-600 mt-1">Manage organizations and import from Workvivo</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openWorkvivoSetup}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all shadow-md"
          >
            <Globe className="w-4 h-4" />
            Import from Workvivo
          </button>
          <button
            onClick={openForCreate}
            className="flex items-center gap-2 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md"
          >
            <Plus className="w-4 h-4" />
            Add Organization
          </button>
        </div>
      </div>

      {/* Organizations List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
        </div>
      ) : organizations.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
          <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500">No organizations yet. Add one or import from Workvivo.</p>
          <div className="flex gap-2 justify-center mt-4">
            <button
              onClick={openWorkvivoSetup}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all shadow-md inline-flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              Import from Workvivo
            </button>
            <button
              onClick={openForCreate}
              className="px-4 py-2 org-gradient org-btn text-white rounded-full transition-all shadow-md inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Organization
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Integration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  SSO
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  SCIM
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Auth Mode
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {organizations.map((org) => {
                const integration = integrationsMap[org.id];
                const sso = integration?.sso;
                const scim = integration?.scim;
                const isExpanded = expandedOrgId === org.id;

                return (
                  <React.Fragment key={org.id}>
                    <tr
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedOrgId(isExpanded ? null : org.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-2xl org-gradient-br flex items-center justify-center mr-3">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <span className="text-sm font-medium text-slate-900">{org.name}</span>
                            {org.description && (
                              <p className="text-xs text-slate-500 mt-0.5">{org.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {org.integration_type === 'workvivo' ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full w-fit">
                            <Globe className="w-3 h-3" />
                            <span className="text-xs font-medium">Workvivo</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 px-2 py-1 bg-slate-100 rounded-full">Internal</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!integration ? (
                          <span className="text-xs text-slate-400">...</span>
                        ) : sso?.configured ? (
                          <div className="flex items-center gap-1 px-2 py-1 rounded-full w-fit" style={{
                            backgroundColor: sso.is_enabled ? '#dcfce7' : '#fef3c7',
                            color: sso.is_enabled ? '#15803d' : '#92400e',
                          }}>
                            <Shield className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              {sso.provider_type === 'azure_ad' ? 'Azure AD' : 'Okta'}
                              {!sso.is_enabled && ' (Off)'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 px-2 py-1 bg-slate-100 rounded-full">Not configured</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!integration ? (
                          <span className="text-xs text-slate-400">...</span>
                        ) : scim?.has_token ? (
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full w-fit">
                            <Key className="w-3 h-3" />
                            <span className="text-xs font-medium">Active</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 px-2 py-1 bg-slate-100 rounded-full">No token</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {org.auth_mode === "sso" ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">SSO Only</span>
                        ) : org.auth_mode === "mixed" ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">Mixed</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">Password</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          org.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}>
                          {org.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setExpandedOrgId(isExpanded ? null : org.id); }}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                            title={isExpanded ? "Collapse" : "Expand details"}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-600" /> : <ChevronDown className="w-4 h-4 text-slate-600" />}
                          </button>
                          {org.integration_type !== 'workvivo' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openConnectWorkvivo(org); }}
                              className="p-1 rounded hover:bg-green-100 transition-colors"
                              title="Connect to Workvivo"
                            >
                              <Globe className="w-4 h-4 text-green-600" />
                            </button>
                          )}
                          {org.integration_type === 'workvivo' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openResyncModal(org); }}
                              className="p-1 rounded hover:bg-green-100 transition-colors"
                              title="Resync users from Workvivo"
                            >
                              <RefreshCw className="w-4 h-4 text-green-600" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); openForEdit(org); }}
                            className="p-1 rounded hover:bg-slate-100 transition-colors"
                            title="Edit organization"
                          >
                            <Edit className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(org.id); }}
                            disabled={deleteMutation.isPending}
                            className="p-1 rounded hover:bg-slate-100 transition-colors disabled:opacity-50"
                            title="Delete organization"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded integration details */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 bg-slate-50/50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* SSO Details */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Shield className="w-4 h-4 text-purple-600" />
                                <h4 className="text-sm font-semibold text-slate-800">Single Sign-On (SSO)</h4>
                              </div>
                              {!integration ? (
                                <p className="text-xs text-slate-400">Loading...</p>
                              ) : sso?.configured ? (
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-slate-500">Provider</span>
                                    <span className="text-xs font-medium text-slate-800">{sso.provider_type === 'azure_ad' ? 'Azure AD' : 'Okta'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-slate-500">Status</span>
                                    <span className={`text-xs font-medium ${sso.is_enabled ? 'text-green-700' : 'text-amber-700'}`}>
                                      {sso.is_enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-xs text-slate-500">Client ID</span>
                                    <span className="text-xs font-mono text-slate-600 truncate ml-2 max-w-[140px]" title={sso.client_id}>{sso.client_id}</span>
                                  </div>
                                  {sso.tenant_id && (
                                    <div className="flex justify-between">
                                      <span className="text-xs text-slate-500">Tenant ID</span>
                                      <span className="text-xs font-mono text-slate-600 truncate ml-2 max-w-[140px]" title={sso.tenant_id}>{sso.tenant_id}</span>
                                    </div>
                                  )}
                                  {sso.okta_domain && (
                                    <div className="flex justify-between">
                                      <span className="text-xs text-slate-500">Okta Domain</span>
                                      <span className="text-xs font-mono text-slate-600 truncate ml-2 max-w-[140px]" title={sso.okta_domain}>{sso.okta_domain}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-xs text-slate-500">Redirect URI</span>
                                    <span className="text-xs font-mono text-slate-600 truncate ml-2 max-w-[140px]" title={sso.redirect_uri}>{sso.redirect_uri}</span>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400">SSO not configured for this organization.</p>
                              )}
                            </div>

                            {/* SCIM Details */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Key className="w-4 h-4 text-blue-600" />
                                <h4 className="text-sm font-semibold text-slate-800">SCIM Provisioning</h4>
                              </div>
                              {!integration ? (
                                <p className="text-xs text-slate-400">Loading...</p>
                              ) : scim?.has_token ? (
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-xs text-slate-500">Token</span>
                                    <span className="text-xs font-medium text-green-700">Active</span>
                                  </div>
                                  <p className="text-xs text-slate-500 mt-2">
                                    SCIM endpoint is active and ready for identity provider integration.
                                  </p>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400">No SCIM token configured. Generate one from the Integrations tab.</p>
                              )}
                            </div>

                            {/* Org Details */}
                            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Building2 className="w-4 h-4 text-slate-600" />
                                <h4 className="text-sm font-semibold text-slate-800">Organization Details</h4>
                              </div>
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-xs text-slate-500">Points (Recv/Send)</span>
                                  <span className="text-xs font-medium text-slate-800">
                                    {org.default_nomination_recipient_points || 0} / {org.default_nomination_sender_points || 0}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs text-slate-500">Recognition Range</span>
                                  <span className="text-xs font-medium text-slate-800">
                                    {org.min_recognition_points || 10} - {org.max_recognition_points || 100} pts
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs text-slate-500">Points Step</span>
                                  <span className="text-xs font-medium text-slate-800">{org.points_step || 10}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-xs text-slate-500">Pts to Dollar Rate</span>
                                  <span className="text-xs font-medium text-slate-800">{org.points_to_dollar_rate || 1.0}</span>
                                </div>
                                {org.import_stats && (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-slate-500">Users</span>
                                      <span className="text-xs font-medium text-slate-800">{org.import_stats.total_users}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-slate-500">Teams</span>
                                      <span className="text-xs font-medium text-slate-800">{org.import_stats.total_teams}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Basic Organization Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={closeModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white">
                <h2 className="text-xl font-semibold text-slate-900">
                  {editingOrg ? "Edit Organization" : "Add Organization"}
                </h2>
              </div>

              <form onSubmit={handleFormSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("name", { required: "Organization name is required" })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Acme Corporation"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register("description")}
                    className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                    placeholder="Optional description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Recipient Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      {...register("default_nomination_recipient_points")}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Sender Points
                    </label>
                    <input
                      type="number"
                      min="0"
                      {...register("default_nomination_sender_points")}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    {...register("is_active")}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-slate-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-slate-700">
                    Active organization
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="flex-1 px-4 py-2 org-gradient org-btn text-white rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {editingOrg ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      editingOrg ? "Update" : "Create"
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Workvivo Setup Modal */}
      <AnimatePresence>
        {showWorkvivoSetup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={closeWorkvivoSetup}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Import from Workvivo</h2>
                    <p className="text-sm text-slate-500">Connect and import users, teams, and spaces</p>
                  </div>
                </div>
              </div>

              {setupResult ? (
                // Setup Result Display
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Setup Complete!</h3>
                    <p className="text-sm text-slate-500">Your Workvivo integration is ready</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{setupResult.users_imported || 0}</p>
                      <p className="text-xs text-blue-600">Users Imported</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <UsersRound className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">{setupResult.teams_imported || 0}</p>
                      <p className="text-xs text-purple-600">Teams Imported</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <Layout className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-emerald-700">{setupResult.spaces_imported || 0}</p>
                      <p className="text-xs text-emerald-600">Spaces Imported</p>
                    </div>
                  </div>

                  {setupResult.organization_id && (
                    <div className="bg-slate-50 rounded-2xl p-4 mb-6">
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Organization ID:</span> {setupResult.organization_id}
                      </p>
                      {setupResult.bot_created && (
                        <p className="text-sm text-green-600 mt-1">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" />
                          Bot configured successfully
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    onClick={closeWorkvivoSetup}
                    className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all"
                  >
                    Done
                  </button>
                </div>
              ) : (
                // Setup Form
                <form onSubmit={handleWorkvivoSubmit(onWorkvivoSubmit)} className="p-6 space-y-6">
                  {/* Organization Info */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organization Information
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Organization Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          {...registerWorkvivo("organization_name", { required: "Organization name is required" })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="e.g. Acme Corp"
                        />
                        {workvivoErrors.organization_name && (
                          <p className="text-xs text-red-500 mt-1">{workvivoErrors.organization_name.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Description
                      </label>
                      <textarea
                        {...registerWorkvivo("organization_description")}
                        className="w-full px-3 py-2 border border-slate-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500"
                        rows="2"
                        placeholder="Optional description..."
                      />
                    </div>
                  </div>

                  {/* Workvivo Credentials */}
                  <div className="space-y-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Workvivo Credentials
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Workvivo Base URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          {...registerWorkvivo("workvivo_base_url", { required: "Base URL is required" })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="https://api.workvivo.com"
                        />
                        {workvivoErrors.workvivo_base_url && (
                          <p className="text-xs text-red-500 mt-1">{workvivoErrors.workvivo_base_url.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Workvivo ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          {...registerWorkvivo("workvivo_id", { required: "Workvivo ID is required" })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Your Workvivo tenant ID"
                        />
                        {workvivoErrors.workvivo_id && (
                          <p className="text-xs text-red-500 mt-1">{workvivoErrors.workvivo_id.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Access Token <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showToken ? "text" : "password"}
                          {...registerWorkvivo("access_token", { required: "Access token is required" })}
                          className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Your Workvivo access token"
                        />
                        <button
                          type="button"
                          onClick={() => setShowToken(!showToken)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                        >
                          {showToken ? (
                            <EyeOff className="w-4 h-4 text-slate-600" />
                          ) : (
                            <Eye className="w-4 h-4 text-slate-600" />
                          )}
                        </button>
                      </div>
                      {workvivoErrors.access_token && (
                        <p className="text-xs text-red-500 mt-1">{workvivoErrors.access_token.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Import Options */}
                  <div className="space-y-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800">Import Options</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          {...registerWorkvivo("import_users")}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-slate-700">Import Users</span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          {...registerWorkvivo("import_teams")}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex items-center gap-2">
                          <UsersRound className="w-4 h-4 text-purple-500" />
                          <span className="text-sm text-slate-700">Import Teams</span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          {...registerWorkvivo("import_spaces")}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex items-center gap-2">
                          <Layout className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm text-slate-700">Import Spaces</span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50">
                        <input
                          type="checkbox"
                          {...registerWorkvivo("setup_bot")}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex items-center gap-2">
                          <Bot className="w-4 h-4 text-orange-500" />
                          <span className="text-sm text-slate-700">Setup Bot</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Points Configuration */}
                  <div className="space-y-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-800">Default Points</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Recipient Points
                        </label>
                        <input
                          type="number"
                          min="0"
                          {...registerWorkvivo("default_nomination_recipient_points")}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Sender Points
                        </label>
                        <input
                          type="number"
                          min="0"
                          {...registerWorkvivo("default_nomination_sender_points")}
                          className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeWorkvivoSetup}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={workvivoSetupMutation.isPending}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {workvivoSetupMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4" />
                          Start Import
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Connect to Workvivo Modal */}
      <AnimatePresence>
        {showConnectWorkvivo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={closeConnectWorkvivo}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Connect to Workvivo</h2>
                    <p className="text-sm text-slate-500">
                      {connectOrg?.name || 'Organization'}
                    </p>
                  </div>
                </div>
              </div>

              {connectResult ? (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Connected!</h3>
                    <p className="text-sm text-slate-500">Workvivo integration is now active</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{connectResult.users_imported || 0}</p>
                      <p className="text-xs text-blue-600">Users Imported</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <UsersRound className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-purple-700">{connectResult.teams_imported || 0}</p>
                      <p className="text-xs text-purple-600">Teams Imported</p>
                    </div>
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <Layout className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-emerald-700">{connectResult.spaces_imported || 0}</p>
                      <p className="text-xs text-emerald-600">Spaces Imported</p>
                    </div>
                  </div>

                  <button
                    onClick={closeConnectWorkvivo}
                    className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleConnectSubmit(onConnectSubmit)} className="p-6 space-y-4">
                  <p className="text-sm text-slate-600 mb-4">
                    Enter your Workvivo credentials to connect this organization.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Workvivo Base URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      {...registerConnect("workvivo_base_url", { required: "Base URL is required" })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://api.workvivo.com"
                    />
                    {connectErrors.workvivo_base_url && (
                      <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_base_url.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Workvivo ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...registerConnect("workvivo_id", { required: "Workvivo ID is required" })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your Workvivo tenant ID"
                    />
                    {connectErrors.workvivo_id && (
                      <p className="text-xs text-red-500 mt-1">{connectErrors.workvivo_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Access Token <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showToken ? "text" : "password"}
                        {...registerConnect("access_token", { required: "Access token is required" })}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Your Workvivo SCIM token"
                      />
                      <button
                        type="button"
                        onClick={() => setShowToken(!showToken)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                      >
                        {showToken ? (
                          <EyeOff className="w-4 h-4 text-slate-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-600" />
                        )}
                      </button>
                    </div>
                    {connectErrors.access_token && (
                      <p className="text-xs text-red-500 mt-1">{connectErrors.access_token.message}</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeConnectWorkvivo}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={connectWorkvivoMutation.isPending}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {connectWorkvivoMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4" />
                          Connect
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Resync Modal */}
      <AnimatePresence>
        {showResyncModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={closeResyncModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Resync from Workvivo</h2>
                    <p className="text-sm text-slate-500">
                      {resyncOrg?.name || 'Organization'}
                    </p>
                  </div>
                </div>
              </div>

              {resyncResult ? (
                // Resync Result Display
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Sync Complete!</h3>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{resyncResult.users_imported || 0}</p>
                      <p className="text-xs text-blue-600">Users Imported</p>
                    </div>
                    <div className="bg-purple-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-700">{resyncResult.teams_imported || 0}</p>
                      <p className="text-xs text-purple-600">Teams</p>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{resyncResult.spaces_imported || 0}</p>
                      <p className="text-xs text-green-600">Spaces</p>
                    </div>
                  </div>

                  {resyncResult.users_deactivated > 0 && (
                    <div className="bg-amber-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-amber-700">
                        <span className="font-semibold">{resyncResult.users_deactivated}</span> users deactivated
                      </p>
                    </div>
                  )}

                  {resyncResult.users_skipped_inactive > 0 && (
                    <div className="bg-gray-50 rounded-2xl p-3 mb-4 text-center">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">{resyncResult.users_skipped_inactive}</span> inactive Workvivo users not imported
                      </p>
                    </div>
                  )}

                  {resyncResult.errors && resyncResult.errors.length > 0 && (
                    <div className="bg-red-50 rounded-2xl p-3 mb-4">
                      <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                      <ul className="text-xs text-red-600 list-disc list-inside">
                        {resyncResult.errors.slice(0, 3).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={closeResyncModal}
                    className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all"
                  >
                    Done
                  </button>
                </div>
              ) : (
                // Resync Form
                <form onSubmit={handleResyncSubmit(onResyncSubmit)} className="p-6 space-y-4">
                  <p className="text-sm text-slate-600 mb-4">
                    Enter your Workvivo credentials to sync users for this organization.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Workvivo Base URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      {...registerResync("workvivo_base_url", { required: "Base URL is required" })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="https://yourcompany.workvivo.com"
                    />
                    {resyncErrors.workvivo_base_url && (
                      <p className="text-xs text-red-500 mt-1">{resyncErrors.workvivo_base_url.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Workvivo ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...registerResync("workvivo_id", { required: "Workvivo ID is required" })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your Workvivo tenant ID"
                    />
                    {resyncErrors.workvivo_id && (
                      <p className="text-xs text-red-500 mt-1">{resyncErrors.workvivo_id.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Access Token <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showToken ? "text" : "password"}
                        {...registerResync("access_token", { required: "Access token is required" })}
                        className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Your Workvivo SCIM token"
                      />
                      <button
                        type="button"
                        onClick={() => setShowToken(!showToken)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                      >
                        {showToken ? (
                          <EyeOff className="w-4 h-4 text-slate-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-600" />
                        )}
                      </button>
                    </div>
                    {resyncErrors.access_token && (
                      <p className="text-xs text-red-500 mt-1">{resyncErrors.access_token.message}</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeResyncModal}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-full hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={resyncMutation.isPending}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-full hover:from-green-700 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {resyncMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Start Sync
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrganizationsSection;
